import {first} from 'lodash';
import {Logger} from '../utils/Logger';
import {Validator} from '../utils/Validator';
import {Scheduler} from '../utils/Scheduler';
import {Component, OnInit} from '@angular/core';
import {Toaster, Type} from '../toaster/Toaster';
import {UserService} from '../service/user.service';
import {FormGroupUtil} from '../utils/FormGroupUtil';
import {AnalyticsTracker} from '../utils/AnalyticsTracker';
import {Keys, StorageService} from '../service/storage.service';
import {UnprocessableEntityError} from '../errors/UnprocessableEntityError';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {UserInteractor} from '../interactors/user.interactor';

@Component({
    selector: 'app-email-verification-form',
    templateUrl: './email-verification-form.component.html'
})

export class EmailVerificationFormComponent implements OnInit {
    public email: string;
    public verificationCode: number;
    public verifyButtonTitle = 'VERIFY';
    public isVerifyButtonDisabled: boolean;
    public emailVerificationRequested: boolean;
    public shouldEmailFieldDisabled: boolean = false;
    emailVerificationRequestGroup: FormGroup;
    emailRegistrationGroup: FormGroup;
    emailFormControl = new FormControl(undefined, [Validators.required, (control: AbstractControl): ValidationErrors => {
        return this.validateEmail(control);
    }]);
    verificationCodeFormControl = new FormControl(undefined, [Validators.required]);
    protected profileDetails: any;
    private validator: Validator;
    private userService: UserService;
    private storageService: StorageService;
    private scheduler: Scheduler;
    private readonly userInteractor: UserInteractor;

    constructor(validator: Validator, userService: UserService,
                formBuilder: FormBuilder, storageService: StorageService, scheduler: Scheduler, public analyticsTracker: AnalyticsTracker,
                userInteractor: UserInteractor) {
        this.validator = validator;
        this.userService = userService;
        this.storageService = storageService;
        this.scheduler = scheduler;
        this.userInteractor = userInteractor;
        this.emailVerificationRequestGroup = formBuilder.group({
            email: this.emailFormControl
        });
        this.emailRegistrationGroup = formBuilder.group({
            emailFormGroup: this.emailVerificationRequestGroup,
            verificationCode: this.verificationCodeFormControl
        });
    }

    private static getErrorMessagesForEmail(): any {
        return {
            email: [{
                type: 'email',
                message: 'Please enter a valid email address'
            }, {
                type: 'required',
                message: 'Please enter an email address'
            }, {
                type: 'email-not-verified',
                message: 'Please verify your email address'
            }],
            verificationCode: [{
                type: 'required',
                message: 'Please enter verification code'
            }, {
                type: 'invalid-verification-code',
                message: 'Please enter valid verification code'
            }]
        };
    }

    async ngOnInit(): Promise<void> {
        this.verifyButtonTitle = 'VERIFY';
        this.isVerifyButtonDisabled = false;
        const profileId = this.storageService.getItem(Keys.PROFILE_ID);
        this.shouldEmailFieldDisabled = this.storageService.getItem('shouldLoanApplicationFlowEmailFieldDisabled') ? true : false;
        this.profileDetails = await this.getProfileDetails(profileId);
        this.email = this.profileDetails.email ? this.profileDetails.email : '';
        if (this.isEmailVerified(this.profileDetails)) {
            this.isVerifyButtonDisabled = true;
        }
    }

    async submitEmailVerificationRequest(formGroup: FormGroup): Promise<void> {
        FormGroupUtil.validateFormGroup(formGroup);
        if (formGroup.invalid) {
            Logger.log(`The form is invalid. Not proceeding with the event.`);
        } else {
            const formData = formGroup.value;
            if (!this.isEmailVerified(this.profileDetails) || (this.profileDetails.email !== formData.email)) {
                this.isVerifyButtonDisabled = true;
                const profileId = this.storageService.getItem(Keys.PROFILE_ID);
                const emailId = formData.email;
                await this.userService.requestForEmailVerification(emailId, profileId);
                this.onSuccessEmailVerificationRequest();
            }
        }
    }

    public onEmailIdChange(): void {
        this.isVerifyButtonDisabled = false;
        this.verifyButtonTitle = 'VERIFY';

        if (this.emailVerificationRequested && this.isEmailAddressModified()) {
            this.emailVerificationRequested = false;
        }
        this.isVerifyButtonDisabled = this.profileDetails.email === this.email ? true : this.isVerifyButtonDisabled;
    }

    public validateEmail(control: AbstractControl): ValidationErrors {
        if (control.value && !(this.validator.isValidEmailFormat(control.value))) {
            return {email: 'Please enter a valid email.'};
        }
    }

    public getErrorMessage(control: FormControl): string {
        const controlName = this.getControlName(control);
        const errors = EmailVerificationFormComponent.getErrorMessagesForEmail();

        if (control.errors) {
            const occurredError = first(Object.keys(control.errors));
            const predefinedErrorsForControl = errors[controlName];
            const result = predefinedErrorsForControl.find(predefinedError => {
                return predefinedError.type === occurredError;
            });
            if (result) {
                return result.message;
            } else {
                Logger.log(`Could not find error message for error: ${occurredError}`);
            }
        }
        return '';
    }

    public async registerEmail(): Promise<void> {
        if (this.isEmailVerified(this.profileDetails) && !this.emailVerificationRequested) {
            if (!this.isEmailAddressModified()) {
                return;
            } else {
                this.emailVerificationRequestGroup.controls.email.setErrors({'email-not-verified': 0});
                throw new Error('Please verify your email address');
            }
        }
        FormGroupUtil.validateFormGroup(this.emailVerificationRequestGroup);
        FormGroupUtil.validateFormGroup(this.emailRegistrationGroup);
        if (this.emailVerificationRequestGroup.invalid || this.emailRegistrationGroup.invalid) {
            throw new Error('Invalid form');
        } else {
            if (this.isEmailAddressModified() && !this.emailVerificationRequested) {
                this.emailVerificationRequestGroup.controls.email.setErrors({'email-not-verified': 0});
                throw new Error('Please verify your email address');
            }
            return this.verifyAndRegisterEmailAddress();
        }
    }

    protected isEmailVerified(profileDetails: any): boolean {
        return !!profileDetails.email_verified;
    }

    protected onSuccessEmailVerificationRequest(): void {
        this.emailVerificationRequested = true;
        this.verifyButtonTitle = 'RESEND';
        this.scheduler.scheduleCallback(30 * 1000, this.enableVerify);
    }

    protected async getProfileDetails(profileId: string): Promise<any> {
        return await this.userService.getProfileDetails(profileId);
    }

    private enableVerify = () => {
        this.isVerifyButtonDisabled = false;
    };

    private async verifyAndRegisterEmailAddress(): Promise<void> {
        const email = this.emailVerificationRequestGroup.value.email;
        const verificationCode = this.emailRegistrationGroup.value.verificationCode;
        const profileId = this.storageService.getItem(Keys.PROFILE_ID);

        try {
            await this.userService.verifyAndRegisterEmail(profileId, email, verificationCode);
            await this.updateEmailAddressAsPrimary(profileId, email);
        } catch (error) {
            if (error instanceof UnprocessableEntityError) {
                this.emailRegistrationGroup.controls.verificationCode.setErrors({'invalid-verification-code': 0});
                this.analyticsTracker.trackError('Wrong verification code entered');
            } else {
                Toaster.toast(`Something went wrong. Please try again later.`, Type.Error);
            }

            throw error;
        }
    }

    private getControlName(c: AbstractControl): string | null {
        const formGroup = c.parent.controls;
        return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
    }

    private isEmailAddressModified() {
        return this.profileDetails.email !== this.email;
    }

    private async  updateEmailAddressAsPrimary(userId: string, emailId: string): Promise<void> {
        const emailAddresses = await this.userInteractor.getEmailAddressesByUserId(userId);
        const emailAddress = emailAddresses.find(email => email.emailId === emailId && !email.primary === true);
        if (emailAddress) {
            await this.userInteractor.markEmailAddressAsPrimary(userId, emailId);
        }
    }
}
